
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { TestResponse, Scores } from "@/types/test-response";
import { calculateTotalScore } from "@/utils/score-utils";
import { useQueryClient } from "@tanstack/react-query";

export function useTestScores(testResponse: TestResponse | undefined) {
  const [scores, setScores] = useState<Scores>({});
  const [isEditing, setIsEditing] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleScoreChange = (questionId: string, score: number) => {
    setScores(prev => ({
      ...prev,
      [questionId]: score
    }));
  };

  // Helper function to dispatch custom events for score updates
  const dispatchScoreUpdateEvent = (testResponseId: string, percentage: number) => {
    window.dispatchEvent(new CustomEvent('scoreUpdated', {
      detail: { 
        testResponseId, 
        percentage,
        timestamp: new Date().toISOString()
      }
    }));
  };

  const handleAcceptAIScores = async () => {
    if (!testResponse) return;
    
    try {
      const updatedScores: Scores = {};
      
      testResponse.question_responses.forEach((qr) => {
        if (qr.ai_feedback?.score !== undefined) {
          updatedScores[qr.question_id] = qr.ai_feedback.score;
        }
      });

      setScores(updatedScores);

      // Calculate total score percentage based on AI scores
      const { percentage } = calculateTotalScore(
        updatedScores, 
        testResponse.test.questions || []
      );

      // First update individual question scores
      await Promise.all(
        Object.entries(updatedScores).map(([questionId, score]) =>
          supabase
            .from("question_responses")
            .update({ score })
            .eq("test_response_id", testResponse.id)
            .eq("question_id", questionId)
        )
      );

      // Then update the overall test response with the calculated percentage
      await supabase
        .from("test_responses")
        .update({ 
          scores_confirmed: true,
          score: percentage  // Store as percentage (0-100)
        })
        .eq("id", testResponse.id);

      // Invalidate related queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ["test-response", testResponse.id]
      });
      
      // If this response is from a test, invalidate the test responses list too
      if (testResponse.test_id) {
        queryClient.invalidateQueries({
          queryKey: ["test-responses", testResponse.test_id]
        });
      }

      // Dispatch custom event for score update
      dispatchScoreUpdateEvent(testResponse.id, percentage);

      toast({
        title: "AI Scores Accepted",
        description: "All AI-suggested scores have been applied successfully.",
      });
      
      setIsEditing(false);
    } catch (error: any) {
      console.error("Error accepting AI scores:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to accept AI scores. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleConfirmScores = async () => {
    if (!testResponse) return;

    try {
      // Validate that we have scores to save
      if (Object.keys(scores).length === 0) {
        toast({
          title: "No scores to save",
          description: "Please assign scores before confirming.",
          variant: "destructive",
        });
        return;
      }

      // Calculate total score percentage
      const { percentage } = calculateTotalScore(
        scores, 
        testResponse.test.questions || []
      );

      // First update individual question scores
      await Promise.all(
        Object.entries(scores).map(([questionId, score]) =>
          supabase
            .from("question_responses")
            .update({ score })
            .eq("test_response_id", testResponse.id)
            .eq("question_id", questionId)
        )
      );

      // Then update the overall test response with the calculated percentage
      await supabase
        .from("test_responses")
        .update({ 
          scores_confirmed: true,
          score: percentage  // Store as percentage (0-100)
        })
        .eq("id", testResponse.id);

      // Invalidate related queries to refresh data
      queryClient.invalidateQueries({
        queryKey: ["test-response", testResponse.id]
      });
      
      // If this response is from a test, invalidate the test responses list too
      if (testResponse.test_id) {
        queryClient.invalidateQueries({
          queryKey: ["test-responses", testResponse.test_id]
        });
      }
      
      // Dispatch custom event for score update
      dispatchScoreUpdateEvent(testResponse.id, percentage);

      toast({
        title: "Scores confirmed",
        description: "The candidate's scores have been saved successfully.",
      });
      
      setIsEditing(false);
    } catch (error: any) {
      console.error("Error confirming scores:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save scores. Please try again.",
        variant: "destructive",
      });
    }
  };

  return {
    scores,
    isEditing,
    setIsEditing,
    handleScoreChange,
    handleAcceptAIScores,
    handleConfirmScores
  };
}
